/**
 * utils/errors.js — Zero-dependency API error formatter.
 *
 * Extracted from admin/shared.js so it can be unit-tested in isolation
 * without pulling axios / lucide-react into the Jest sandbox.
 */

/**
 * Safely stringify a FastAPI error response into a user-facing message.
 *
 * FastAPI returns `detail` as:
 *   • a plain string for `HTTPException` (our happy path),
 *   • an ARRAY of `{type, loc, msg, input, url}` objects for 422
 *     validation errors (which sonner cannot render as React children —
 *     crashes the app with "Objects are not valid as a React child").
 *
 * This helper normalises both shapes into a string so `toast.error()`
 * always receives safe text. Falls back to `fallback` if no detail is
 * present.
 */
export function formatApiError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic v2 validation errors → join the human-readable `msg`
    // fields, prefixed with the location where available.
    return detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.join(".") : null;
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" · ");
  }
  // Object that isn't a validation array (rare — custom endpoint payloads).
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}
